import React, { useState, useEffect } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  Shield,
  Users,
  Key,
  Settings,
  CheckCircle,
  Building
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import roleService from '../../services/roleService'
import useToast from '../../hooks/useToast'
// Consolidated CSS is imported globally'

const RoleMaster = () => {
  const navigate = useNavigate()
  const { success, error } = useToast()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalRoles: 0,
    activeRoles: 0,
    inactiveRoles: 0,
    totalUsers: 0
  })

  // Fetch roles data
  useEffect(() => {
    fetchRoles()
    fetchStats()
  }, [])

  const fetchRoles = async () => {
    try {
      setLoading(true)
      const response = await roleService.getAllRoles()
      if (response.success) {
        const transformedData = response.data.map(role => roleService.transformRoleForDisplay(role))
        setData(transformedData)
      }
    } catch (err) {
      error('Failed to fetch roles: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await roleService.getRoleStats()
      if (response.success) {
        setStats(response.data)
      }
    } catch (err) {
      console.error('Failed to fetch role stats:', err)
    }
  }

  const columns = [
    {
      accessorKey: 'roleCode',
      header: 'Role Code',
      size: 120,
      cell: ({ getValue }) => (
        <div className="role-code-cell">
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'roleName',
      header: 'Role Name',
      size: 150,
      cell: ({ getValue }) => (
        <div className="role-name-cell">
          <span className="name-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: 200,
      cell: ({ getValue }) => (
        <div className="description-cell">
          <span className="description-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'hierarchy',
      header: 'Hierarchy',
      size: 100,
      cell: ({ getValue }) => (
        <div className="hierarchy-cell">
          <span className="hierarchy-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ getValue }) => (
        <div className="status-cell">
          <span className={`status-badge ${getValue().toLowerCase()}`}>
            {getValue()}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'permissions',
      header: 'Permissions',
      size: 150,
      cell: ({ getValue }) => {
        const permissions = getValue()
        if (!permissions || !Array.isArray(permissions)) {
          return (
            <div className="permissions-cell">
              <span className="permissions-text">No permissions</span>
            </div>
          )
        }
        
        const permissionCount = permissions.length
        const moduleCount = permissions.reduce((acc, perm) => {
          return acc + (perm.actions ? perm.actions.length : 0)
        }, 0)
        
        return (
          <div className="permissions-cell">
            <span className="permissions-text">
              {permissionCount} modules, {moduleCount} actions
            </span>
          </div>
        )
      },
    },
    {
      accessorKey: 'users',
      header: 'Users',
      size: 80,
      cell: ({ getValue }) => (
        <div className="users-cell">
          <span className="users-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'lastModified',
      header: 'Last Modified',
      size: 120,
      cell: ({ getValue }) => (
        <div className="last-modified-cell">
          <span className="last-modified-text">{getValue()}</span>
        </div>
      ),
    },
  ]

  const handleBack = () => {
    navigate('/settings?tab=masters')
  }

  const handleAddNew = () => {
    navigate('/settings/role-master/form')
  }

  const handleExport = () => {
    // Handle export
    console.log('Export Role data')
    success('Export functionality will be implemented soon')
  }

  const handleEdit = (roleData) => {
    navigate('/settings/role-master/edit', { state: { roleData } })
  }

  const handleView = (roleData) => {
    navigate('/settings/role-master/view', { state: { roleData } })
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Role Master',
      path: '/settings/role-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Role Master</h1>
          <p>Manage user roles and permissions</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Shield size={16} />
            </div>
            <div className="metric-trend positive">
              <span>+{stats.totalRoles}</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalRoles}</div>
          <div className="metric-title">Total Roles</div>
          <div className="metric-subtitle">All defined roles</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>+{stats.activeRoles}</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeRoles}</div>
          <div className="metric-title">Active Roles</div>
          <div className="metric-subtitle">Currently in use</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>{stats.inactiveRoles}</span>
            </div>
          </div>
          <div className="metric-value">{stats.inactiveRoles}</div>
          <div className="metric-title">Inactive Roles</div>
          <div className="metric-subtitle">Disabled or archived</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Building size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>{stats.totalUsers}</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalUsers}</div>
          <div className="metric-title">Total Users</div>
          <div className="metric-subtitle">Users assigned to roles</div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={data}
        columns={columns}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        enableSelection={true}
        enablePagination={true}
        pageSize={10}
        onView={handleView}
        onEdit={handleEdit}
        noCard={true}
        loading={loading}
        className="sac-master-table"
      />
    </div>
  )
}

export default RoleMaster
